import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Stack, alpha, useTheme } from '@mui/material';
import { DataGridPro, type GridColDef, type GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { MappingResult } from '@verantos-dev/feature-code-mapping';
import {
  ConceptGroupMetaStrip,
  ProjectDataGrid,
  type MetaStripItem,
} from '@verantos-dev/feature-concept-group-detail';

import {
  MAPPING_STATUS,
  useConceptGroupMappingColumns,
  type ConceptGroupMappingRow,
} from '../hooks/useConceptGroupMappingColumns';

const ROW_HEIGHT = 52;

/** Fixed status grouping (Figma order), independent of any column sort. */
const STATUS_ORDER: Record<string, number> = {
  'not found': 0,
  unmapped: 1,
  mapped: 2,
  'already standard': 3,
};
const statusOrder = (status: string): number => STATUS_ORDER[status.trim().toLowerCase()] ?? 4;

/**
 * Mapping Results panel for the concept-group draft review — the source→mapped
 * audit for a map-first upload (OR-6288). Built on the SAME base as the Concepts
 * tab (the {@link ConceptGroupMetaStrip} + a {@link ProjectDataGrid}) so the two
 * tabs look identical, with the mapping specifics layered on: a Status pill
 * column, the Figma column order/labels, expandable Source→Mapped rows, and a
 * subtle tint on retained "Not found" rows only (the legacy grid fills every
 * unmapped row a heavy solid red).
 */
export const ConceptGroupMappingPanel = ({
  results,
  testIdPrefix,
  onDownload,
}: {
  results: MappingResult[];
  testIdPrefix: string;
  onDownload?: () => void;
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const columns = useConceptGroupMappingColumns();

  // Source-level counts for the strip: a "Not found" source never resolved to
  // any concept; everything else (Mapped / Unmapped / Already standard) was found.
  const notFound = results.filter((row) => row.status.trim().toLowerCase() === 'not found').length;
  const found = results.length - notFound;

  const rows: ConceptGroupMappingRow[] = useMemo(
    () =>
      results
        .map((result, index) => ({ ...result, rowId: `${index}-${result.id}` }))
        .sort((a, b) => statusOrder(a.status || '') - statusOrder(b.status || '')),
    [results]
  );

  // Nested (mapped-target) grid columns: a spacer matching the detail-toggle
  // column so the child columns line up under the parent's data columns.
  const detailColumns = useMemo<GridColDef<ConceptGroupMappingRow>[]>(
    () => [
      { field: '__spacer__', headerName: '', width: 50, sortable: false, resizable: false },
      ...columns,
    ],
    [columns]
  );

  const metaItems: MetaStripItem[] = [
    {
      key: 'found',
      icon: <CheckCircleIcon fontSize="small" color="success" />,
      label: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.conceptsFoundLabel' }),
      value: found,
    },
    {
      key: 'notFound',
      icon: <ErrorIcon fontSize="small" color="error" />,
      label: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.conceptsNotFoundLabel' }),
      value: notFound,
    },
  ];

  const renderMappedChildren = (row: ConceptGroupMappingRow) => {
    const children = row.mappedRows;
    if (!row.isCollapsible || !children || children.length === 0) return null;
    const height = children.length * ROW_HEIGHT;
    return (
      <Box sx={{ bgcolor: 'grey.50', height, width: '100%' }}>
        <DataGridPro
          rows={children.map((child, idx) => ({
            ...child,
            rowId: `${row.rowId}-child-${idx}`,
            status: '', // children are mapped targets — no status pill
          }))}
          columns={detailColumns}
          getRowId={(child) => child.rowId}
          rowHeight={ROW_HEIGHT}
          hideFooter
          disableColumnMenu
          disableColumnResize
          columnHeaderHeight={0}
          sx={{
            border: 'none',
            height,
            '& .MuiDataGrid-columnHeaders': { display: 'none' },
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
          }}
        />
      </Box>
    );
  };

  return (
    <Stack
      sx={{ gap: 1.5, height: '100%', minHeight: 0, paddingBottom: 6 }}
      data-testid={`${testIdPrefix}-mapping-panel`}
    >
      <ConceptGroupMetaStrip
        items={metaItems}
        onDownload={onDownload}
        testIdPrefix={`${testIdPrefix}-mapping`}
      />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ProjectDataGrid<ConceptGroupMappingRow>
          id={`${testIdPrefix}-mapping`}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.rowId}
          // Match the Concepts grid's density so the two tabs' headers (and
          // overall grid metrics) read identically.
          density="compact"
          rowHeight={ROW_HEIGHT}
          disableColumnMenu
          layoutSx={{ height: '100%' }}
          getDetailPanelContent={(params: GridRowParams<ConceptGroupMappingRow>) =>
            renderMappedChildren(params.row)
          }
          getDetailPanelHeight={(params: GridRowParams<ConceptGroupMappingRow>) =>
            params.row.isCollapsible && params.row.mappedRows?.length
              ? params.row.mappedRows.length * ROW_HEIGHT
              : 0
          }
          getRowClassName={(params) => {
            const classes: string[] = [];
            if (params.row.status?.trim() === MAPPING_STATUS.notFound) {
              classes.push('cg-mapping-not-found');
            }
            if (!params.row.isCollapsible) classes.push('cg-mapping-non-collapsible');
            return classes.join(' ');
          }}
          slots={{
            detailPanelExpandIcon: ChevronRightIcon,
            detailPanelCollapseIcon: ExpandMoreIcon,
          }}
          sx={{
            // Retained "Not found" rows get a subtle tint (Figma) — not the
            // heavy full-row fill the legacy grid uses for every unmapped row.
            '& .MuiDataGrid-row.cg-mapping-not-found': {
              backgroundColor: alpha(theme.palette.error.main, 0.08),
            },
            // Non-collapsible rows (Not found / Unmapped / Already standard) have
            // no mapped children, so hide their expand chevron.
            '& .cg-mapping-non-collapsible .MuiDataGrid-detailPanelToggleCell': {
              display: 'none',
            },
          }}
        />
      </Box>
    </Stack>
  );
};
